import React, { useState } from "react";
import { Link2, Download, Copy, Check, Loader2, Globe2, Smartphone, Code2, FileJson } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ShareExportDialog({ gameId, status, open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [building, setBuilding] = useState(false);
  const [downloadingJson, setDownloadingJson] = useState(false);
  const published = status === "published";
  const shareUrl = `${window.location.origin}/app/arcade/${gameId}`;
  const embedCode = `<iframe src="${shareUrl}" width="480" height="720" style="border:0;border-radius:16px" allow="fullscreen; gamepad; autoplay" title="GameView Studio game"></iframe>`;

  const copy = async (text, setter) => {
    try { await navigator.clipboard.writeText(text); setter(true); setTimeout(() => setter(false), 1800); }
    catch { toast.error("Couldn't copy — select and copy manually."); }
  };

  const downloadWeb = async () => {
    setBuilding(true);
    try {
      const { data } = await api.post(`/games/${gameId}/export-web`);
      const a = document.createElement("a");
      a.href = `${BACKEND}${data.url}`; a.download = data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Web build ready — ${data.filename} 🌐`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBuilding(false); }
  };

  const downloadJson = async () => {
    setDownloadingJson(true);
    try {
      const { data } = await api.get(`/bridge/spec/${gameId}`);
      const blob = new Blob([JSON.stringify(data.build_spec, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${gameId}.gameview.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Project JSON downloaded 📄");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDownloadingJson(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-export-dialog">
        <DialogHeader>
          <DialogTitle>Share & Export</DialogTitle>
          <DialogDescription>Share with a link, embed it on any site, or download a standalone build.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl border border-[hsl(var(--border))] p-3.5">
            <div className="flex items-center gap-2 font-semibold text-sm mb-1.5"><Link2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Share link</div>
            {published ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-[hsl(var(--muted))] rounded-lg px-2.5 py-2 truncate" data-testid="share-url">{shareUrl}</code>
                <Button size="icon" variant="outline" onClick={() => copy(shareUrl, setCopied)} className="rounded-lg shrink-0" data-testid="copy-share-link">
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Publish your game first to get a public Arcade link anyone can play.</p>
            )}
          </div>

          <div className="rounded-xl border border-[hsl(var(--border))] p-3.5">
            <div className="flex items-center gap-2 font-semibold text-sm mb-1.5"><Code2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Embed on your site</div>
            {published ? (
              <>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">Paste this snippet into any blog or website to embed the playable game.</p>
                <div className="flex items-start gap-2">
                  <code className="flex-1 text-[11px] leading-relaxed bg-[hsl(var(--muted))] rounded-lg px-2.5 py-2 break-all max-h-24 overflow-y-auto" data-testid="embed-code">{embedCode}</code>
                  <Button size="icon" variant="outline" onClick={() => copy(embedCode, setEmbedCopied)} className="rounded-lg shrink-0" data-testid="copy-embed-code">
                    {embedCopied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            ) : (
              <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="embed-locked-note">Publish your game first to get an embeddable snippet.</p>
            )}
          </div>

          <div className="rounded-xl border border-[hsl(var(--border))] p-3.5">
            <div className="flex items-center gap-2 font-semibold text-sm mb-1.5"><Globe2 className="w-4 h-4 text-[hsl(var(--primary))]" /> Web build (.zip)</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2.5">A self-contained folder you can host anywhere (Netlify, itch.io, GitHub Pages) or run locally.</p>
            <Button onClick={downloadWeb} disabled={building || !published} className="w-full rounded-full" data-testid="download-web-build">
              {building ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : <><Download className="w-4 h-4" /> Download web build</>}
            </Button>
            {!published && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2" data-testid="web-build-locked-note">Publish your game first to download a web build.</p>}
          </div>

          <div className="rounded-xl border border-[hsl(var(--border))] p-3.5">
            <div className="flex items-center gap-2 font-semibold text-sm mb-1.5"><FileJson className="w-4 h-4 text-[hsl(var(--primary))]" /> Project JSON</div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2.5">Download the full Build Spec (world, entities, assets, lighting) as a portable <code>.json</code> file for backups or engine import.</p>
            <Button onClick={downloadJson} disabled={downloadingJson} variant="outline" className="w-full rounded-full" data-testid="download-project-json">
              {downloadingJson ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <><Download className="w-4 h-4" /> Download project JSON</>}
            </Button>
          </div>

          <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <Smartphone className="w-4 h-4 shrink-0 mt-0.5" />
            <span><b className="text-[hsl(var(--foreground))]">PC / Mobile apps:</b> the web build is a complete static web app — wrap it with a PWA shell, Capacitor (mobile) or Tauri/Electron (desktop) to ship installable apps.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
